import { Component, OnInit, ViewChild } from "@angular/core";
import { ZXingScannerComponent } from "@zxing/ngx-scanner";
import { AssetService } from "@app/_services";
import { ITuple } from "@app/_models/tuple";
import { Asset, AssetTypeName } from "@app/_models/_DTOs/asset";
import { AssetType } from "@app/_models/enums/assetType";
import { AssetViewModel } from "@app/_models/_viewModels/asset.viewmodel";
import { UploadFile, NzMessageService } from "ng-zorro-antd";
import { Media } from "@app/_models/_DTOs/media";
import { environment } from "@environments/environment.prod";
import { HttpEventType } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { InventoryService } from "@app/_services/inventory.service";
import { Inventory } from "@app/_models/_DTOs/inventory";
import { InventoryAsset } from "@app/_models/_DTOs/inventoryAsset";
import { InventoryAssetsComponent } from "@app/inventory-assets/inventory-assets.component";
import { InventoryAssetService } from "@app/_services/inventoryAsset.service";
import { AssetEditorComponent } from "@app/assets/asset-editor/asset-editor.component";
import { map } from "rxjs/operators";
import { InventoryAssetAction } from "@app/_models/_DTOs/inventoryAssetActionDto";
import { InventoryAssetActionViewModel } from "@app/_models/_viewModels/inventoryAssetActionViewModel";
import { InventoryCompletion } from "@app/_models/inventoryCompletion";
import { AuthenticationService } from "@app/_services";
import { UserToken } from "@app/_models";
import { UserInventoryCompletion } from "@app/_models/userInventoryCompletion";
import { InventoryFoundAssetViewModel } from "@app/_models/_viewModels/inventoryFoundAsset.viewmodel";
import { InventoryFoundAsset } from "@app/_models/_DTOs/inventoryFoundAsset";
import { InventoryAssetsByBarCodeResponse } from "@app/_models/_response/inventoryAssetsByBarCode.response";
import { NumberHelper } from "@app/_helpers/number.helper";
import { AssetHelper } from "@app/_helpers/asset.helper";

@Component({
  selector: "app-inventory",
  templateUrl: "./inventory.component.html",
  styleUrls: ["./inventory.component.css"],
})
export class InventoryComponent implements OnInit {
  @ViewChild("scanner", { static: false })
  scanner: ZXingScannerComponent;
  @ViewChild("newScanner", { static: false })
  newScanner: ZXingScannerComponent;
  allStepsComplete: boolean = false;

  progressFormat = (percent: number) => {
    return `${Math.round((percent / 100) * this.steps.length)} / ${this.steps.length
      }`;
  };

  inventoryId: number;
  inventory: Inventory;

  inventoryCompletion: InventoryCompletion;
  inventoryCompletionForAllUsers: UserInventoryCompletion[];

  listIsOpen: boolean = false;
  @ViewChild(InventoryAssetsComponent, { static: false })
  private inventoryAssetsComponent: InventoryAssetsComponent;

  @ViewChild(AssetEditorComponent, { static: false })
  private assetEditorComponent: AssetEditorComponent;

  steps: string[] = ["Odabir artikla", "Fotografiranje", "Dodatne informacije"];
  stepNumber: number = 0;
  status: string = "normal";
  allowedFormats = [
    "CODE_128"
  ];

  scannerInitialized: boolean = false;
  scannerEnabled: boolean = true;
  torchCompatible: boolean = false;
  torchEnabled: boolean = false;
  scannedCode: string = "";
  manualCodeInput: string = "";
  scannerError: string = "";
  torch: boolean = false;

  newScannerInitialized: boolean = false;
  newScannerEnabled: boolean = false;
  newScannedCode: string = "";
  newScannerError: boolean = false;

  assetSearchResult: InventoryAsset[];
  inventoryFoundAssetSearchResult: InventoryFoundAsset[];
  selectedInventoryAsset: InventoryAsset;
  assetViewModel: AssetViewModel = null;
  editAssetViewModel: AssetViewModel = null;
  foundAssetViewModel: InventoryFoundAssetViewModel = null;

  conflictingAssets: Asset[];
  recommendedDiscarding: boolean = false;
  remark: string = "";

  showUploadList = {
    showPreviewIcon: true,
    showRemoveIcon: true,
    hidePreviewIconInNonImage: true,
  };
  fileList: UploadFile[] = [];
  didDeleteMedia: boolean = false;
  previewImage: string | undefined = "";
  previewVisible = false;
  uploadPercentage: number = 0;

  apiUrl: string;

  isSavingAsset: boolean = false;
  signingInProgress: boolean = false;
  errorMessages = {};

  currentUser: UserToken = null;

  constructor(
    private assetService: AssetService,
    private inventoryAssetService: InventoryAssetService,
    private messageService: NzMessageService,
    private route: ActivatedRoute,
    private inventoryService: InventoryService,
    private authenticationService: AuthenticationService
  ) {
    this.apiUrl = environment.apiUrl;
    this.authenticationService.currentUserToken.subscribe((userToken) => {
      this.currentUser = userToken;
    });
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.inventoryId = +params["id"];
      this.inventoryService
        .get(this.inventoryId)
        .subscribe((data: Inventory) => {
          this.inventory = data;
          this.getInventoryCompletionForUser();
          if (
            this.currentUser &&
            (this.currentUser.roles[0] == "admin" ||
              this.currentUser.roles[0] == "superadmin")
          ) {
            this.getInventoryCompletion();
          }
        });
    });
    this.enableScanner();
  }

  getInventoryCompletionForUser() {
    if (this.inventoryId != undefined) {
      this.inventoryService
        .getCompletionForUser(this.inventoryId)
        .subscribe((completion) => {
          this.inventoryCompletion = completion;
        });
    }
  }

  getInventoryCompletion() {
    if (this.inventoryId != undefined) {
      this.inventoryService
        .getCompletion(this.inventoryId)
        .subscribe((completion) => {
          this.inventoryCompletionForAllUsers = completion;
        });
    }
  }

  completionFormat = (percent: number) => `${percent.toFixed(1)} %`;

  onStepChange(step: number) {
    if (step == 1) {
      this.didDeleteMedia = false;
    }
  }

  enableScanner() {
    this.scannerError = "";
    this.scannerInitialized = false;
    this.scannerEnabled = true;
    setTimeout(() => {
      this.scannerInitialized = true;
    }, 1000);
  }

  disableScanner() {
    this.scannerError = "";
    this.scannerInitialized = false;
    this.scannerEnabled = false;
  }

  onTorchCompatible(event: any) {
    this.torchCompatible = event as boolean;
  }

  toggleTorch() {
    if (this.torchCompatible) {
      this.torch = !this.torch;
    }
  }

  enableNewScanner() {
    this.newScannerError = false;
    this.newScannerInitialized = false;
    this.newScannerEnabled = true;
    setTimeout(() => {
      this.newScannerInitialized = true;
    }, 1000);
  }

  disableNewScanner() {
    this.newScannerError = false;
    this.newScannerInitialized = false;
    this.newScannerEnabled = false;
  }

  scanSuccessHandler(scanResult: string) {
    this.scannedCode = scanResult;
    if (scanResult) {
      if (scanResult.length > 0) {
        this.inventoryService
          .searchInventoryAssets(this.inventoryId, scanResult)
          .subscribe((data: InventoryAssetsByBarCodeResponse) => {
            if (data) {
              this.disableScanner();
              this.assetSearchResult = data.inventoryAssets;
              this.inventoryFoundAssetSearchResult = data.inventoryFoundAssets;
            }
          });
      }
    }
  }

  manualScanHandler() {
    if (this.manualCodeInput) {
      if (this.manualCodeInput.length > 0) {
        this.inventoryService
          .searchInventoryAssets(this.inventoryId, this.manualCodeInput)
          .subscribe((data: InventoryAssetsByBarCodeResponse) => {
            if (data) {
              this.manualCodeInput = "";
              this.disableScanner();
              this.assetSearchResult = data.inventoryAssets;
              this.inventoryFoundAssetSearchResult = data.inventoryFoundAssets;
            }
          });
      }
    }
  }


  openNewScanner() {
    this.enableNewScanner();
  }

  closeNewScanner() {
    this.disableNewScanner();
  }

  newScanSuccessHandler(scanResult: string) {
    // this.newScannedCode = scanResult;
    // if (scanResult) {
    //   if (scanResult.length > 0) {
    //     let action: InventoryAssetActionViewModel = {
    //       inventoryAssetId: this.selectedInventoryAsset.id,
    //       newBarCode: this.newScannedCode,
    //       scannedBarCode: this.scannedCode,
    //       recommendedDiscarding: this.recommendedDiscarding,
    //       remark: this.remark
    //     };
    //     this.inventoryAssetService.signInventoryAsset(action).subscribe(result => {
    //       this.newScannerError = false;
    //       this.messageService.create("success", "Bar kod uspjšeno zabilježen.");
    //       this.disableNewScanner();
    //       this.allStepsComplete = true;
    //       this.status = "success";
    //     }, error => {
    //       this.messageService.create("error", error.message);
    //       this.newScannerError = true;
    //     });
    //   }
    // }
    if (scanResult) {
      if (scanResult.length > 0) {
        this.newScannedCode = scanResult;
        this.assetService
          .getAssetsByBarcode(this.newScannedCode, this.assetViewModel.id)
          .subscribe((assetsWithSameBarcode) => {
            assetsWithSameBarcode.subscribe((conflictingAssetNames) => {
              if (conflictingAssetNames && conflictingAssetNames.length > 0) {
                this.messageService.create("error", "Bar kod je već zauzet.");
                this.newScannedCode = "";
                this.newScannerError = true;
              } else {
                this.newScannerError = false;
                this.messageService.create(
                  "success",
                  "Bar kod uspjšeno zabilježen."
                );
                this.disableNewScanner();
              }
            });
          });
      }
    }
  }

  manuallySetBarCode() {
    if (this.newScannedCode && this.newScannedCode.length > 0) {
      this.assetService
        .getAssetsByBarcode(this.newScannedCode, this.assetViewModel.id)
        .subscribe((assetsWithSameBarcode) => {
          assetsWithSameBarcode.subscribe((conflictingAssetNames) => {
            if (conflictingAssetNames && conflictingAssetNames.length > 0) {
              this.messageService.create("error", "Bar kod je već zauzet.");
              this.newScannedCode = "";
              this.newScannerError = true;
            } else {
              this.newScannerError = false;
              this.messageService.create(
                "success",
                "Bar kod uspjšeno zabilježen."
              );
              this.disableNewScanner();
            }
          });
        });
    }
  }

  signAsset() {
    if (!this.signingInProgress) {
      this.signingInProgress = true;
      this.disableNewScanner();
      if (this.selectedInventoryAsset) {
        let newBarCode = null;
        if (this.newScannedCode && this.newScannedCode.length > 0) {
          newBarCode = this.newScannedCode;
        }
        let action: InventoryAssetActionViewModel = {
          inventoryAssetId: this.selectedInventoryAsset.id,
          newBarCode: newBarCode,
          scannedBarCode: this.scannedCode,
          recommendedDiscarding: this.recommendedDiscarding,
          remark: this.remark,
        };
        this.inventoryAssetService.signInventoryAsset(action).subscribe(
          (result) => {
            this.messageService.create(
              "success",
              "Artikl uspješno zabilježen."
            );
            this.signingInProgress = false;
            this.allStepsComplete = true;
            this.status = "success";
            this.newScannedCode = "";
            this.getInventoryCompletionForUser();
            if (
              this.currentUser &&
              (this.currentUser.roles[0] == "admin" ||
                this.currentUser.roles[0] == "superadmin")
            ) {
              this.getInventoryCompletion();
            }
          },
          (error) => {
            this.newScannedCode = "";
            this.signingInProgress = false;
            this.messageService.create("error", error.message);
            this.enableNewScanner();
          }
        );
      }
    }
  }

  resetSteps() {
    this.enableScanner();
    this.assetSearchResult = null;
    this.stepNumber = 0;
    this.selectedInventoryAsset = null;
    this.allStepsComplete = false;
    this.status = "normal";
    this.recommendedDiscarding = false;
    this.newScannedCode = "";
    this.remark = "";
  }

  openAssetList() {
    this.inventoryAssetsComponent.searchData(true);
    this.inventoryAssetsComponent.loadDataForDropdowns();
    this.disableScanner();
    this.listIsOpen = true;
  }

  closeAssetList() {
    if (this.selectedInventoryAsset == null) {
      this.enableScanner();
    }
    this.listIsOpen = false;
  }

  resetScan() {
    this.enableScanner();
    this.manualCodeInput = "";
    this.assetSearchResult = null;
  }

  finishScan(inventoryAsset: InventoryAsset) {
    this.selectedInventoryAsset = inventoryAsset;
    this.disableScanner();
    this.assetService
      .getAssetsWithConflictingInventoryCode(
        inventoryAsset.asset,
        inventoryAsset.asset.department.legalEntity.id,
        inventoryAsset.asset.type
      )
      .subscribe((conflictingAssets) => {
        this.conflictingAssets = conflictingAssets;
        this.assetViewModel = this.initModel(inventoryAsset.asset);
        this.stepNumber++;
      });
  }

  back() {
    this.stepNumber--;
    if (this.stepNumber == 0) {
      this.assetSearchResult = null;
      this.enableScanner();
    }
    if (this.stepNumber == 1) {
      this.refreshAssetViewModel();
    }
    if (this.stepNumber != 2) {
      this.disableNewScanner();
    }
  }

  uploadPhotos() {
    if (this.fileList.length > 0 || this.didDeleteMedia) {
      this.isSavingAsset = true;
      this.uploadPercentage = 0;
      const formData = new FormData();
      this.fileList.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("model", JSON.stringify(this.assetViewModel));

      // this.newScannerEnabled = true;
      this.assetService
        .putWithUpload(formData, this.assetViewModel.id)
        .subscribe(
          (event) => {
            if (event.type === HttpEventType.UploadProgress)
              this.uploadPercentage = Math.round(
                (100 * event.loaded) / event.total
              );
            else if (event.type === HttpEventType.Response) {
              this.messageService.create(
                "success",
                "Imovina uspješno izmijenjena"
              );
              this.isSavingAsset = false;
              this.uploadPercentage = 0;
              this.refreshAssetViewModel();
              this.stepNumber++;
            }
          },
          (error) => {
            if (error.ParamName) {
              var errorMessage = error.Message.split("Parameter name")[0];
              this.errorMessages[error.ParamName] = errorMessage;
            }
            this.isSavingAsset = false;
            this.disableNewScanner();
          }
        );
    } else {
      this.stepNumber++;
    }
  }

  private getAssetTypeName(assetType: AssetType) {
    return AssetTypeName.get(assetType);
  }

  initModel(asset?: Asset) {
    this.fileList = [];
    if (asset != undefined) {
      var assetViewModel = {
        id: asset.id ? asset.id : -1,
        type: asset.type ? asset.type : undefined,
        inventoryCode: asset.inventoryCode
          ? NumberHelper.pad(
            parseInt(AssetHelper.ParseInventoryCodeNumber(asset)),
            4
          )
          : "",
        inventoryCodeNumber: asset.inventoryCodeNumber
          ? asset.inventoryCodeNumber
          : 0,
        barCode: asset.barCode ? asset.barCode : "",
        name: asset.name ? asset.name : "",
        description: asset.description ? asset.description : "",
        serialNumber: asset.serialNumber ? asset.serialNumber : "",
        qty: asset.qty ? asset.qty : 1,
        purchasePrice: asset.purchasePrice ? asset.purchasePrice : 0,
        currentValue: asset.currentValue ? asset.currentValue : 0,
        purchaseDate: asset.purchaseDate ? asset.purchaseDate : new Date(),
        notes: asset.notes ? asset.notes : "",
        condition: asset.condition ? asset.condition : undefined,
        retiredDate: asset.retiredDate ? asset.retiredDate : undefined,
        warrantyExpirationDate: asset.warrantyExpirationDate
          ? asset.warrantyExpirationDate
          : undefined,
        manufacturer: asset.manufacturer ? asset.manufacturer : "",
        media: asset.media ? asset.media : undefined,
        departmentId: asset.department ? asset.department.id : undefined,
        employeeId: asset.employee ? asset.employee.id : undefined,
        assetGroupId: asset.assetGroup ? asset.assetGroup.id : undefined,
        locationId: asset.location ? asset.location.id : undefined,
        supplierId: asset.supplier ? asset.supplier.id : undefined,
      };
      return assetViewModel;
    } else {
      var viewModel = {
        id: -1,
        type: undefined,
        inventoryCode: "",
        inventoryCodeNumber: 0,
        barCode: "",
        name: "",
        description: "",
        serialNumber: "",
        qty: 1,
        purchasePrice: 0,
        currentValue: 0,
        purchaseDate: new Date(),
        notes: "",
        condition: undefined,
        retiredDate: undefined,
        warrantyExpirationDate: undefined,
        manufacturer: "",
        media: undefined,
        departmentId: undefined,
        employeeId: undefined,
        assetGroupId: undefined,
        locationId: undefined,
        supplierId: undefined,
      };
      return viewModel;
    }
  }

  initFoundAssetModel(asset?: InventoryFoundAsset) {
    if (this.inventoryId != undefined) {
      this.fileList = [];
      if (asset != undefined) {
        var foundAssetViewModel = {
          id: asset.id ? asset.id : -1,
          inventoryId: this.inventoryId,
          type: asset.type ? asset.type : undefined,
          barCode: asset.barCode ? asset.barCode : "",
          name: asset.name ? asset.name : "",
          description: asset.description ? asset.description : "",
          serialNumber: asset.serialNumber ? asset.serialNumber : "",
          qty: asset.qty ? asset.qty : 1,
          purchasePrice: asset.purchasePrice ? asset.purchasePrice : 0,
          purchaseDate: asset.purchaseDate ? asset.purchaseDate : new Date(),
          notes: asset.notes ? asset.notes : "",
          condition: asset.condition ? asset.condition : undefined,
          retiredDate: asset.retiredDate ? asset.retiredDate : undefined,
          warrantyExpirationDate: asset.warrantyExpirationDate
            ? asset.warrantyExpirationDate
            : undefined,
          manufacturer: asset.manufacturer ? asset.manufacturer : "",
          media: asset.media ? asset.media : undefined,
          departmentId: asset.department ? asset.department.id : undefined,
          employeeId: asset.employee ? asset.employee.id : undefined,
          assetGroupId: asset.assetGroup ? asset.assetGroup.id : undefined,
          locationId: asset.location ? asset.location.id : undefined,
          supplierId: asset.supplier ? asset.supplier.id : undefined,
        };
        return foundAssetViewModel;
      } else {
        var viewModel = {
          id: -1,
          inventoryId: this.inventoryId,
          type: undefined,
          inventoryCode: "",
          inventoryCodeNumber: 0,
          barCode: "",
          name: "",
          description: "",
          serialNumber: "",
          qty: 1,
          purchasePrice: 0,
          purchaseDate: new Date(),
          notes: "",
          condition: undefined,
          retiredDate: undefined,
          warrantyExpirationDate: undefined,
          manufacturer: "",
          media: undefined,
          departmentId: undefined,
          employeeId: undefined,
          assetGroupId: undefined,
          locationId: undefined,
          supplierId: undefined,
        };
        return viewModel;
      }
    }
    return null;
  }

  editAsset() {
    this.assetEditorComponent.fileList = this.fileList;
    this.editAssetViewModel = this.assetViewModel;
  }

  addNewFoundAsset() {
    this.foundAssetViewModel = this.initFoundAssetModel();
  }

  refreshAssetViewModel() {
    this.assetService.get(this.assetViewModel.id).subscribe(
      (data: Asset) => {
        this.assetViewModel = this.initModel(data);
      },
      (error) => {
        this.messageService.error("Došlo je do greške pri spremanju podataka.");
        console.log(error);
      }
    );
  }

  closeEditDrawer(reload: boolean) {
    this.refreshAssetViewModel();
    this.editAssetViewModel = null;
  }

  closeFoundAssetEditDrawer(reload: boolean) {
    // TODO
    this.foundAssetViewModel = null;
  }

  handlePreview = (file: UploadFile) => {
    this.previewImage = file.url || file.thumbUrl;
    this.previewVisible = true;
  };

  previewMedia(media: Media) {
    this.previewImage = `${this.apiUrl}/files/${media.path}`;
    this.previewVisible = true;
  }

  beforeUpload = (file: UploadFile): boolean => {
    this.fileList = this.fileList.concat(file);
    return false;
  };

  deleteAssetMedia(assetMediaItem: Media) {
    this.didDeleteMedia = true;
    this.assetViewModel.media = this.assetViewModel.media.filter(
      (item) => item !== assetMediaItem
    );
  }
}
